"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;

var _ssrWindow = require("ssr-window");

var _forEach = _interopRequireDefault(require("lodash/forEach"));

var _isArray = _interopRequireDefault(require("lodash/isArray"));

var _isArrayLikeObject = _interopRequireDefault(require("lodash/isArrayLikeObject"));

var _isString = _interopRequireDefault(require("lodash/isString"));

var _includes = _interopRequireDefault(require("lodash/includes"));

(function () {
  var enterModule = require('react-hot-loader').enterModule;

  enterModule && enterModule(module);
})();

/**
 * Utilities for working with the Document Object Model (DOM).
 * @module DOMUtils
 */
var DOMUtils = {
  /**
   * Returns the tag name of the specified element.
   * @static
   * @function tag
   * @param {Element} el - The element to check the tag of.
   * @param {Boolean} [lowercase=true] - Whether to normalize the tag name for comparison as lowercase.
   * @example
   * DOMUtils.tag(document.querySelector('.foo'));
   * @returns {String} - The tag of the specified element.
   */
  tag: function tag(el) {
    var lowercase = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
    return lowercase ? el.nodeName.toLowerCase() : el.nodeName;
  },

  /**
   * Returns the element(s) matching the specified selector.
   * @static
   * @function el
   * @param {String} selector - The selector of the element to query.
   * @example
   * DOMUtils.el('.foo');
   * @returns {Element|Array} - The element or array of elements matching the selector.
   */
  el: function el(selector) {
    var els = _ssrWindow.document.querySelectorAll(selector);

    if (els.length > 1) {
      return els;
    } else {
      return els[0];
    }
  },

  /**
   * Returns the element's parent or the closest ancestor matching the specified selector if provided.
   * @static
   * @function parent
   * @param {String|Element} item - The selector or element to find the parent of.
   * @param {String} [parentSelector] - The selector of the ancestor to find.
   * @example
   * DOMUtils.parent('.foo', '.bar');
   * @returns {Element} - The parent of the item.
   */
  parent: function parent(item, parentSelector) {
    var el = (0, _isString.default)(item) ? DOMUtils.el(item) : item;

    if (!el) {
      return;
    }

    if (parentSelector) {
      var allParents = _ssrWindow.document.querySelectorAll(parentSelector);

      var currentParent = el.parentNode;

      while (currentParent && !(0, _includes.default)(allParents, currentParent)) {
        currentParent = currentParent.parentNode;
      }

      return currentParent;
    } else {
      return el.parentNode;
    }
  },

  /**
   * Returns the attribute of the specified element.
   * @static
   * @function attr
   * @param {String|Element} item - The selector or element to query.
   * @param {String} attribute - The attribute to query.
   * @example
   * DOMUtils.attr('.foo', 'data-baz');
   * @returns {String} - The value of the attribute.
   */
  attr: function attr(item, attribute) {
    var el = (0, _isString.default)(item) ? DOMUtils.el(item) : item;

    if (!el) {
      return;
    }

    return el.getAttribute(attribute);
  },

  /**
   * Adds a class to the specified element.
   * @static
   * @function addClass
   * @param {String|Element} item - The selector or element to add the class to.
   * @param {String|Array.<String>} className - The class(es) to add.
   * @example
   * DOMUtils.addClass(evt.currentTarget, 'foo');
   * @returns {void}
   */
  addClass: function addClass(item, className) {
    var el = (0, _isString.default)(item) ? DOMUtils.el(item) : item;

    if (!el) {
      return;
    }

    var classes = (0, _isString.default)(className) ? className.split(' ') : className;
    classes.forEach(function (itemClassName) {
      el.classList.add(itemClassName);
    });
  },

  /**
   * Removes a class from the specified element.
   * @static
   * @function removeClass
   * @param {String|Element} item - The selector or element to remove the class from.
   * @param {String|Array.<String>} className - The class(es) to remove.
   * @example
   * DOMUtils.removeClass(evt.currentTarget, 'foo');
   * @returns {void}
   */
  removeClass: function removeClass(item, className) {
    var el = (0, _isString.default)(item) ? DOMUtils.el(item) : item;

    if (!el) {
      return;
    }

    var classes = (0, _isString.default)(className) ? className.split(' ') : className;
    classes.forEach(function (itemClassName) {
      el.classList.remove(itemClassName);
    });
  },

  /**
   * Checks if an element has a specified class.
   * @static
   * @function hasClass
   * @param {String|Element} item - The selector or element to check.
   * @param {String} className - The class to check for.
   * @example
   * DOMUtils.hasClass(evt.currentTarget, 'foo');
   * @returns {Boolean} - Whether the element has the class or not.
   */
  hasClass: function hasClass(item, className) {
    var el = (0, _isString.default)(item) ? DOMUtils.el(item) : item;

    if (!el) {
      return false;
    }

    return el.classList.contains(className) || false;
  },

  /**
   * Toggles a class on or off for the specified element.
   * @static
   * @function toggleClass
   * @param {String|Element} item - The selector or element to toggle the class on.
   * @param {String} className - The class to toggle.
   * @example
   * DOMUtils.toggleClass(evt.currentTarget, 'foo');
   * @returns {void}
   */
  toggleClass: function toggleClass(item, className) {
    if (DOMUtils.hasClass(item, className)) {
      DOMUtils.removeClass(item, className);
    } else {
      DOMUtils.addClass(item, className);
    }
  },

  /**
   * Get the height of the specified element.
   * @static
   * @function height
   * @param {String|Element} item - The selector or element to find the height of.
   * @param {Boolean} [includeMargin=true] - Whether to include the margins in the calculation effectively getting the outerHeight.
   * @param {Boolean} [includePadding=true] - Whether to include the padding in the calculation.
   * @example
   * DOMUtils.height('.foo');
   * @returns {Number} - The height of the element.
   */
  height: function height(item) {
    var includeMargin = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
    var includePadding = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;
    var el = (0, _isString.default)(item) ? DOMUtils.el(item) : item;

    if (!el) {
      return 0;
    }

    var _window$getComputedSt = _ssrWindow.window.getComputedStyle(el),
        marginTop = _window$getComputedSt.marginTop,
        marginBottom = _window$getComputedSt.marginBottom,
        paddingTop = _window$getComputedSt.paddingTop,
        paddingBottom = _window$getComputedSt.paddingBottom;

    var height = el.offsetHeight;

    if (includeMargin) {
      height += parseInt(marginTop, 10) + parseInt(marginBottom, 10);
    }

    if (!includePadding) {
      height -= parseInt(paddingTop, 10) + parseInt(paddingBottom, 10);
    }

    return height;
  },

  /**
   * Get the width of the specified element.
   * @static
   * @function width
   * @param {String|Element} item - The selector or element to find the width of.
   * @param {Boolean} [includeMargin=true] - Whether to include the margins in the calculation effectively getting the outerWidth.
   * @param {Boolean} [includePadding=true] - Whether to include the padding in the calculation.
   * @example
   * DOMUtils.width('.foo');
   * @returns {Number} - The width of the element.
   */
  width: function width(item) {
    var includeMargin = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
    var includePadding = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;
    var el = (0, _isString.default)(item) ? DOMUtils.el(item) : item;

    if (!el) {
      return 0;
    }

    var _window$getComputedSt2 = _ssrWindow.window.getComputedStyle(el),
        marginLeft = _window$getComputedSt2.marginLeft,
        marginRight = _window$getComputedSt2.marginRight,
        paddingLeft = _window$getComputedSt2.paddingLeft,
        paddingRight = _window$getComputedSt2.paddingRight;

    var width = el.offsetWidth;

    if (includeMargin) {
      width += parseInt(marginLeft, 10) + parseInt(marginRight, 10);
    }

    if (!includePadding) {
      width -= parseInt(paddingLeft, 10) + parseInt(paddingRight, 10);
    }

    return width;
  },

  /**
   * Removes element(s) from the DOM.
   * @static
   * @function remove
   * @param {String|Element|Array} items - The selector or element(s) to remove from the DOM.
   * @example
   * DOMUtils.remove('.class-to-remove');
   * @returns {void}
   */
  remove: function remove(items) {
    var el = (0, _isString.default)(items) ? DOMUtils.el(items) : items;

    if (!el) {
      return;
    }

    if ((0, _isArray.default)(el) || el instanceof NodeList) {
      var len = el.length;
      var i = 0;

      for (i; i < len; ++i) {
        el.outerHTML = '';
      }
    } else {
      el.outerHTML = '';
    }
  },

  /**
   * Adds event listener for an element or NodeList.
   * @static
   * @function addEventListener
   * @param {Object} data - The data to pass to the function.
   * @param {String} data.event - The DOM event to take action on.
   * @param {String|Element} [data.target='document'] - The selector or element to attach the event to.
   * @param {Function} data.handler - The function to run when the event triggers.
   * @example
   * DOMUtils.addEventListener({target: '.foo', event: 'click', handler: functionName});
   * @returns {void}
   */
  addEventListener: function addEventListener(_ref) {
    var event = _ref.event,
        _ref$target = _ref.target,
        target = _ref$target === void 0 ? 'document' : _ref$target,
        handler = _ref.handler;

    if (target && target !== 'document') {
      var el = (0, _isString.default)(target) ? DOMUtils.el(target) : target;

      if (!el) {
        return;
      }

      if ((0, _isArrayLikeObject.default)(el)) {
        (0, _forEach.default)(el, function (thisEl) {
          thisEl.addEventListener(event, handler);
        });
      } else {
        el.addEventListener(event, handler);
      }
    } else {
      _ssrWindow.document.addEventListener(event, handler);
    }
  },

  /**
   * Removes event listener for an element or NodeList.
   * @static
   * @function removeEventListener
   * @param {Object} data - The data to pass to the function.
   * @param {String} data.event - The DOM event to remove.
   * @param {String|Element} [data.target='document'] - The selector or element to remove the event from.
   * @param {Function} data.handler - The function that is triggered to handle the event.
   * @example
   * DOMUtils.removeEventListener({target: '.foo', event: 'click', handler: functionName});
   * @returns {void}
   */
  removeEventListener: function removeEventListener(_ref2) {
    var event = _ref2.event,
        _ref2$target = _ref2.target,
        target = _ref2$target === void 0 ? 'document' : _ref2$target,
        handler = _ref2.handler;

    if (target && target !== 'document') {
      var el = (0, _isString.default)(target) ? DOMUtils.el(target) : target;

      if (!el) {
        return;
      }

      if ((0, _isArrayLikeObject.default)(el)) {
        (0, _forEach.default)(el, function (thisEl) {
          thisEl.removeEventListener(event, handler);
        });
      } else {
        el.removeEventListener(event, handler);
      }
    } else {
      _ssrWindow.document.removeEventListener(event, handler);
    }
  },

  /**
   * Creates and dispatches a custom event, with or without data.
   * @static
   * @function sendCustomEvent
   * @param {String} eventName - The name for the custom event.
   * @param {Object} [data=null] - The data to send with the custom event.
   * @example
   * DOMUtils.sendCustomEvent('open-my-mind', {mind: 'free'});
   * @returns {void}
   */
  sendCustomEvent: function sendCustomEvent(eventName) {
    var data = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;

    if (!eventName) {
      return;
    }

    var customEvent = null;

    if (_ssrWindow.window.CustomEvent) {
      customEvent = new CustomEvent(eventName, {
        detail: data
      });
    } else {
      customEvent = _ssrWindow.document.createEvent('CustomEvent');
      customEvent.initCustomEvent(eventName, true, true, data);
    }

    _ssrWindow.document.dispatchEvent(customEvent);
  },

  /**
   * Gets the offset of an element relative to the document.
   * @static
   * @function getDocumentOffset
   * @param {String|Element} item - The selector or element to get the offset of.
   * @example
   * DOMUtils.getDocumentOffset('.foo');
   * @returns {Object} - The top and left values of the offset.
   */
  getDocumentOffset: function getDocumentOffset(item) {
    var el = (0, _isString.default)(item) ? DOMUtils.el(item) : item;
    var rect = el.getBoundingClientRect();
    var scrollLeft = _ssrWindow.window.pageXOffset || _ssrWindow.document.documentElement.scrollLeft;
    var scrollTop = _ssrWindow.window.pageYOffset || _ssrWindow.document.documentElement.scrollTop;
    return {
      top: rect.top + scrollTop,
      left: rect.left + scrollLeft
    };
  }
};
var _default = DOMUtils;
var _default2 = _default;
exports.default = _default2;
;

(function () {
  var reactHotLoader = require('react-hot-loader').default;

  var leaveModule = require('react-hot-loader').leaveModule;

  if (!reactHotLoader) {
    return;
  }

  reactHotLoader.register(DOMUtils, "DOMUtils", "/Users/mprzybylski/Work/SpotHero/fe-utils/src/dom/dom.js");
  reactHotLoader.register(_default, "default", "/Users/mprzybylski/Work/SpotHero/fe-utils/src/dom/dom.js");
  leaveModule(module);
})();

;